/**
 * MemberReviewPanel — unified member-import review (Import_Data_PRD.md).
 *
 * Always shown before commit (replaces the old stats-only ReviewPanel + the
 * errors-only MemberFixPanel for members):
 *   - Stat summary (Total / Valid / Errors).
 *   - A per-row list. Rows needing attention (errors or warnings) render as
 *     editable cards with inline typo suggestions; clean rows render as compact
 *     read-only lines. Error-first ordering, scrollable, with a filter to show
 *     only rows needing attention vs. all rows.
 * On import, posts ALL rows (corrected + untouched) via execute-from-payload;
 * the server re-validates and skips/fails any still-bad rows.
 */

import { useMemo, useState } from 'react';
import { Button } from '../ui/Button';
import { getSuggestedEmail } from '../../lib/emailValidation';
import type { MemberValidationResponse, CsvRow, CorrectedRow } from '../../services/imports.service';
import { FileText, XCircle, AlertTriangle, CheckCircle } from 'lucide-react';

const EDIT_FIELDS: Array<{ key: string; label: string; type?: 'select' }> = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'email', label: 'Email' },
  { key: 'gender', label: 'Gender', type: 'select' },
  { key: 'department_name', label: 'Department' },
  { key: 'team_name', label: 'Team' },
  { key: 'date_of_birth', label: 'Date of birth' },
  { key: 'date_joined', label: 'Date joined' },
];

function norm(s: string): string {
  return (s || '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9]/g, '');
}
function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return d[m][n];
}
/** Closest candidate by normalized edit distance, or null if nothing is close. */
function closest(input: string, candidates: string[]): string | null {
  const ni = norm(input);
  if (!ni) return null;
  let best: string | null = null;
  let bestD = Infinity;
  for (const c of candidates) {
    const d = levenshtein(ni, norm(c));
    if (d < bestD) { bestD = d; best = c; }
  }
  if (best === null) return null;
  const threshold = Math.max(2, Math.floor(ni.length * 0.34));
  return bestD > 0 && bestD <= threshold ? best : null;
}

const fullName = (r: CsvRow) => [r.data.first_name, r.data.last_name].filter(Boolean).join(' ') || '—';

export function MemberReviewPanel({ validation, deptNames, teamNames, onCancel, onImport, importing }: {
  validation: MemberValidationResponse;
  deptNames: string[];
  teamNames: string[];
  onCancel: () => void;
  onImport: (rows: CorrectedRow[]) => void;
  importing: boolean;
}) {
  const [rows, setRows] = useState<CsvRow[]>(() => validation.rows.map((r) => ({ ...r, data: { ...r.data } })));
  const [filter, setFilter] = useState<'attention' | 'all'>(
    validation.error_rows > 0 || validation.warnings.length > 0 ? 'attention' : 'all',
  );

  const errorRowNums = useMemo(() => new Set(validation.errors.map((e) => e.row)), [validation]);
  const errorsByRow = useMemo(() => {
    const m = new Map<number, string[]>();
    validation.errors.forEach((e) => m.set(e.row, [...(m.get(e.row) ?? []), e.message]));
    return m;
  }, [validation]);
  const warningsByRow = useMemo(() => {
    const m = new Map<number, string[]>();
    validation.warnings.forEach((w) => m.set(w.row, [...(m.get(w.row) ?? []), w.message]));
    return m;
  }, [validation]);
  const deptSet = useMemo(() => new Set(deptNames.map(norm)), [deptNames]);
  const teamSet = useMemo(() => new Set(teamNames.map(norm)), [teamNames]);

  const needsAttention = (r: CsvRow) => errorRowNums.has(r.row_number) || warningsByRow.has(r.row_number);

  const update = (rowNum: number, field: string, value: string) =>
    setRows((prev) => prev.map((r) => (r.row_number === rowNum ? { ...r, data: { ...r.data, [field]: value } } : r)));

  const emailSug = (r: CsvRow) => getSuggestedEmail(r.data.email || '');
  const deptSug = (r: CsvRow) => {
    const v = r.data.department_name || '';
    return v && !deptSet.has(norm(v)) ? closest(v, deptNames) : null;
  };
  const teamSug = (r: CsvRow) => {
    const v = r.data.team_name || '';
    return v && !teamSet.has(norm(v)) ? closest(v, teamNames) : null;
  };

  const applyAll = () => {
    setRows((prev) => prev.map((r) => {
      if (!needsAttention(r)) return r;
      const d = { ...r.data };
      const e = getSuggestedEmail(d.email || ''); if (e) d.email = e;
      const dv = d.department_name || ''; if (dv && !deptSet.has(norm(dv))) { const c = closest(dv, deptNames); if (c) d.department_name = c; }
      const tv = d.team_name || ''; if (tv && !teamSet.has(norm(tv))) { const c = closest(tv, teamNames); if (c) d.team_name = c; }
      return { ...r, data: d };
    }));
  };

  // Error-first ordering: errors → warnings → clean, then by row number.
  const ordered = useMemo(() => {
    const rank = (r: CsvRow) => (errorRowNums.has(r.row_number) ? 0 : warningsByRow.has(r.row_number) ? 1 : 2);
    return [...rows].sort((a, b) => rank(a) - rank(b) || a.row_number - b.row_number);
  }, [rows, errorRowNums, warningsByRow]);

  const attentionCount = useMemo(() => rows.filter(needsAttention).length, [rows, errorRowNums, warningsByRow]); // eslint-disable-line react-hooks/exhaustive-deps
  const visible = filter === 'attention' ? ordered.filter(needsAttention) : ordered;
  const suggestionCount = rows.reduce(
    (n, r) => (needsAttention(r) ? n + (emailSug(r) ? 1 : 0) + (deptSug(r) ? 1 : 0) + (teamSug(r) ? 1 : 0) : n), 0,
  );

  const TabBtn = ({ value, label }: { value: 'attention' | 'all'; label: string }) => (
    <button
      type="button"
      onClick={() => setFilter(value)}
      className={`px-3 py-1.5 text-sm rounded-md ${filter === value ? 'bg-blue-600 text-white' : 'text-body hover:bg-surface-hover'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="bg-surface rounded-xl p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-heading">Review — Members</h2>
        {suggestionCount > 0 && (
          <Button variant="secondary" onClick={applyAll}>Apply all suggestions ({suggestionCount})</Button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Total Rows" value={validation.total_rows} tone="neutral" />
        <StatCard label="Valid Rows" value={validation.valid_rows} tone="success" />
        <StatCard label="Error Rows" value={validation.error_rows} tone="danger" />
      </div>

      <div className="flex items-center gap-2">
        <div className="inline-flex gap-1 rounded-lg bg-surface-app p-1">
          <TabBtn value="attention" label={`Needs attention (${attentionCount})`} />
          <TabBtn value="all" label={`All rows (${rows.length})`} />
        </div>
        <p className="text-xs text-meta">
          {attentionCount === 0
            ? 'All rows look good — review and import.'
            : 'Fix the red (error) rows — the import is all-or-nothing, so nothing is saved until they are resolved. Amber (warning) rows will simply be skipped. Corrections are re-checked on the server.'}
        </p>
      </div>

      <div className="max-h-[28rem] overflow-y-auto pr-1 rounded-lg divide-y divide-default">
        {visible.length === 0 ? (
          <p className="px-3 py-6 text-sm text-meta text-center">No rows need attention 🎉</p>
        ) : (
          visible.map((r) =>
            needsAttention(r) ? (
              <div key={r.row_number} className="py-2">
                <EditableRow
                  r={r}
                  errors={errorsByRow.get(r.row_number) ?? []}
                  warnings={warningsByRow.get(r.row_number) ?? []}
                  emailSug={emailSug(r)}
                  deptSug={deptSug(r)}
                  teamSug={teamSug(r)}
                  onUpdate={update}
                />
              </div>
            ) : (
              <CompactRow key={r.row_number} r={r} />
            ),
          )
        )}
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button
          onClick={() => onImport(rows.map((r) => ({ row_number: r.row_number, data: r.data })))}
          isLoading={importing}
        >
          {validation.error_rows > 0 ? 'Import corrected data' : `Start import (${validation.valid_rows} rows)`}
        </Button>
      </div>
    </div>
  );
}

// ---------- compact read-only row (clean rows) ----------
const CompactRow = ({ r }: { r: CsvRow }) => (
  <div className="flex items-center gap-3 px-3 py-2 text-sm">
    <span className="w-8 shrink-0 text-xs text-meta">{r.row_number}</span>
    <span className="w-40 shrink-0 truncate text-body">{fullName(r)}</span>
    <span className="flex-1 truncate text-meta">{r.data.email || '—'}</span>
    <span className="hidden md:block w-48 shrink-0 truncate text-meta">
      {[r.data.department_name, r.data.team_name].filter(Boolean).join(' · ') || '—'}
    </span>
    <span className="hidden md:block w-24 shrink-0 text-right text-xs text-meta">{r.data.date_joined || ''}</span>
  </div>
);

// ---------- editable card (rows with errors / warnings) ----------
const EditableRow = ({ r, errors, warnings, emailSug, deptSug, teamSug, onUpdate }: {
  r: CsvRow;
  errors: string[];
  warnings: string[];
  emailSug: string | null;
  deptSug: string | null;
  teamSug: string | null;
  onUpdate: (rowNum: number, field: string, value: string) => void;
}) => {
  const isError = errors.length > 0;
  const border = isError ? 'border-status-danger' : 'border-status-warning';
  const bannerBg = isError ? 'bg-status-danger-subtle' : 'bg-status-warning-subtle';
  const bannerText = isError ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300';
  const messages = isError ? errors : warnings;
  return (
    <div className={`rounded-lg border ${border} overflow-hidden`}>
      <div className={`${bannerBg} ${bannerText} px-3 py-2 text-sm font-semibold flex items-center gap-2`}>
        {isError ? <XCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />} Row {r.row_number}
        {messages.length > 0 && <span>— {messages.join('; ')}</span>}
      </div>
      <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3">
        {EDIT_FIELDS.map((f) => (
          <label key={f.key} className="text-xs text-meta">
            {f.label}
            {f.type === 'select' ? (
              <select
                className="mt-1 w-full border border-default rounded px-2 py-1 text-sm bg-surface text-body"
                value={r.data[f.key] ?? ''}
                onChange={(e) => onUpdate(r.row_number, f.key, e.target.value)}
              >
                <option value="">—</option>
                <option value="male">male</option>
                <option value="female">female</option>
              </select>
            ) : (
              <input
                className="mt-1 w-full border border-default rounded px-2 py-1 text-sm bg-surface text-body"
                value={r.data[f.key] ?? ''}
                onChange={(e) => onUpdate(r.row_number, f.key, e.target.value)}
              />
            )}
          </label>
        ))}
      </div>
      {(emailSug || deptSug || teamSug) && (
        <div className="px-3 pb-3 space-y-1">
          {emailSug && <Suggest label={`Did you mean "${emailSug}"?`} onApply={() => onUpdate(r.row_number, 'email', emailSug)} />}
          {deptSug && <Suggest label={`Department — did you mean "${deptSug}"?`} onApply={() => onUpdate(r.row_number, 'department_name', deptSug)} />}
          {teamSug && <Suggest label={`Team — did you mean "${teamSug}"?`} onApply={() => onUpdate(r.row_number, 'team_name', teamSug)} />}
        </div>
      )}
    </div>
  );
};

const Suggest = ({ label, onApply }: { label: string; onApply: () => void }) => (
  <div className="flex items-center gap-2 text-sm">
    <span className="text-amber-700">💡 {label}</span>
    <button type="button" onClick={onApply} className="text-blue-600 dark:text-blue-400 hover:underline text-xs font-medium">Apply</button>
  </div>
);

const STAT_TONES = {
  neutral: { bg: 'bg-surface-app', value: 'text-heading', label: 'text-meta', icon: 'text-meta', Icon: FileText },
  success: { bg: 'bg-status-success-subtle', value: 'text-green-700 dark:text-green-300', label: 'text-green-500', icon: 'text-green-500', Icon: CheckCircle },
  danger: { bg: 'bg-status-danger-subtle', value: 'text-red-700 dark:text-red-300', label: 'text-red-500', icon: 'text-red-500', Icon: XCircle },
} as const;
const StatCard = ({ label, value, tone }: { label: string; value: number; tone: keyof typeof STAT_TONES }) => {
  const t = STAT_TONES[tone];
  const Icon = t.Icon;
  return (
    <div className={`rounded-lg ${t.bg} p-4 text-center`}>
      <Icon className={`mx-auto h-6 w-6 ${t.icon}`} />
      <div className={`mt-1 text-2xl font-bold ${t.value}`}>{value}</div>
      <div className={`text-xs ${t.label}`}>{label}</div>
    </div>
  );
};
